import { handleAndMonitorError } from '../helpers';

interface StackMonitorConfig {
  warningThreshold?: number; // Percentage of stack used before warning (default: 80%)
  criticalThreshold?: number; // Percentage of stack used before critical (default: 90%)
  checkInterval?: number; // How often to check in ms (default: 1000)
}

// Conservative frame budget used as the denominator for stack-usage reporting.
// The stack limit must never be measured by recursing until RangeError: when such a
// probe starts from an already-deep stack, the RangeError escapes the surrounding
// try/catch and surfaces as an unhandled "Maximum call stack size exceeded" crash.
const DEFAULT_MAX_STACK_DEPTH = 8000;

class StackMonitor {
  private static instance: StackMonitor;
  private warningThreshold: number;
  private criticalThreshold: number;
  private checkInterval: number;
  private intervalId?: number;
  private maxStackDepth: number;
  private hasWarned = false;
  private hasCritical = false;

  private constructor(config: StackMonitorConfig = {}) {
    this.warningThreshold = config.warningThreshold || 0.8;
    this.criticalThreshold = config.criticalThreshold || 0.9;
    this.checkInterval = config.checkInterval || 1000;
    this.maxStackDepth = DEFAULT_MAX_STACK_DEPTH;
  }

  static getInstance(config?: StackMonitorConfig): StackMonitor {
    if (!StackMonitor.instance) {
      StackMonitor.instance = new StackMonitor(config);
    }
    return StackMonitor.instance;
  }

  // Get current stack depth
  private getCurrentStackDepth(): number {
    const stack = new Error().stack || '';
    return (stack.match(/\n/g) || []).length;
  }

  // Check stack usage and warn if necessary
  checkStackUsage(): void {
    const currentDepth = this.getCurrentStackDepth();
    const usage = currentDepth / this.maxStackDepth;

    if (usage >= this.criticalThreshold && !this.hasCritical) {
      this.hasCritical = true;
      handleAndMonitorError(
        `CRITICAL: Stack usage at ${Math.round(usage * 100)}% - Potential stack overflow imminent!`,
        {
          currentDepth,
          maxStackDepth: this.maxStackDepth,
          usage: Math.round(usage * 100),
          stackTrace: new Error().stack,
        }
      );
    } else if (usage >= this.warningThreshold && !this.hasWarned) {
      this.hasWarned = true;
      handleAndMonitorError(
        `WARNING: Stack usage at ${Math.round(usage * 100)}% - Consider optimizing recursive calls`,
        {
          currentDepth,
          maxStackDepth: this.maxStackDepth,
          usage: Math.round(usage * 100),
        }
      );
    } else if (usage < this.warningThreshold) {
      // Reset warnings if stack usage drops
      this.hasWarned = false;
      this.hasCritical = false;
    }
  }

  // Start monitoring
  startMonitoring(): void {
    if (this.intervalId) return;

    this.intervalId = window.setInterval(() => {
      this.checkStackUsage();
    }, this.checkInterval);
  }

  // Stop monitoring
  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  // Manual check for critical paths
  static checkNow(): void {
    const monitor = StackMonitor.getInstance();
    monitor.checkStackUsage();
  }
}

// Export singleton instance
export const stackMonitor = StackMonitor.getInstance();

// Export the class for testing
export { StackMonitor };
